/* Draw Poker.
 *
 * This file deals nothing. The service shuffles the deck before the first card
 * is shown and scores the hand at the end; the page sends which cards were
 * held and renders the reply. That matters for the claim the page makes —
 * editing anything here changes the animation, not the cards.
 *
 * shuffle() below is a faithful port of game.shuffle() on the server,
 * rejection sampling included. It exists so the proof is something the
 * visitor's own browser performs on all 52 cards rather than something we
 * assert about one.
 */
(function () {
  'use strict';

  var RANKS = '23456789TJQKA';
  var SUITS = 'shdc';
  var PIP = { s: '♠', h: '♥', d: '♦', c: '♣' };
  var LONG = { T: '10', J: 'J', Q: 'Q', K: 'K', A: 'A' };

  function deckOrder() {
    var cards = [];
    for (var s = 0; s < SUITS.length; s += 1) {
      for (var r = 0; r < RANKS.length; r += 1) cards.push(RANKS[r] + SUITS[s]);
    }
    return cards;
  }

  function enc(text) { return new TextEncoder().encode(text); }

  function hex(buffer) {
    return Array.prototype.map.call(new Uint8Array(buffer), function (b) {
      return b.toString(16).padStart(2, '0');
    }).join('');
  }

  function sha256Hex(text) {
    return crypto.subtle.digest('SHA-256', enc(text)).then(hex);
  }

  function hmac(keyText, messageText) {
    return crypto.subtle
      .importKey('raw', enc(keyText), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'])
      .then(function (key) { return crypto.subtle.sign('HMAC', key, enc(messageText)); });
  }

  /* The server's shuffle, reproduced. Fisher-Yates over an HMAC byte stream,
   * discarding draws that fall outside the largest exact multiple of the
   * range — a plain modulo would lean the shuffle toward low indices, which
   * nobody would ever see and which would make this proof worthless. */
  function shuffle(serverSeed, clientSeed, nonce) {
    var deck = deckOrder();
    var pool = [];
    var counter = 0;

    function fill() {
      return hmac(serverSeed, clientSeed + ':' + nonce + ':' + counter)
        .then(function (digest) {
          var view = new DataView(digest);
          for (var offset = 0; offset + 4 <= digest.byteLength; offset += 4) {
            pool.push(view.getUint32(offset, false));
          }
          counter += 1;
        });
    }

    function step(i) {
      if (i <= 0) return Promise.resolve(deck);
      var span = i + 1;
      var limit = Math.floor(4294967296 / span) * span;

      function take() {
        if (!pool.length) return fill().then(take);
        var number = pool.shift();
        if (number >= limit) return take();
        var j = number % span;
        var keep = deck[i];
        deck[i] = deck[j];
        deck[j] = keep;
        return step(i - 1);
      }
      return take();
    }
    return step(deck.length - 1);
  }

  function browserId() {
    var key = 'tms-poker-id';
    var id;
    try {
      id = localStorage.getItem(key);
      if (!id) {
        id = hex(crypto.getRandomValues(new Uint8Array(12)).buffer);
        localStorage.setItem(key, id);
      }
    } catch (error) {
      // Private mode or blocked storage. A per-visit id still plays; it just
      // cannot remember that this browser already had its hand today.
      id = hex(crypto.getRandomValues(new Uint8Array(12)).buffer);
    }
    return id;
  }

  function table(root) {
    var endpoint = (root.dataset.endpoint || '/apps/tms/game').replace(/\/$/, '');
    var seats = Array.prototype.slice.call(root.querySelectorAll('[data-seat]'));
    var action = root.querySelector('[data-action]');
    var hintButton = root.querySelector('[data-hint]');
    var status = root.querySelector('[data-status]');
    var result = root.querySelector('[data-result]');
    var handRow = root.querySelector('[data-hand]');

    var round = null;
    var hand = [];
    var held = {};
    var advice = [];
    var phase = 'ready';           // ready -> dealt -> done
    var idle = status ? status.textContent : '';

    function say(text) { if (status) status.textContent = text; }

    function proxy(path, body) {
      var url = endpoint + path + '?browser=' + encodeURIComponent(browserId());
      var options = body
        ? { method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body) }
        : { method: 'GET' };
      return fetch(url, options).then(function (response) {
        if (response.ok) return response.json();
        /* The backend answers errors with a status and a JSON reason
           (2026-09-29): 400 bad email / wrong answer / bad holds, 404 expired
           round, 409 draw before deal, 429 limits, 502 mint failed, 503
           {closed:true} when the game is off. Show its reason. A failed
           skill-testing answer resolves, so the claim form can re-select the
           answer field as before. */
        return response.json().catch(function () { return null; }).then(function (data) {
          if (data && data.skill_failed) return data;
          var why = data && typeof data.error === 'string' && data.error;
          if (!why && data && data.closed) why = 'The table is closed right now.';
          throw new Error(why || 'Something went wrong at the table. Try again in a moment.');
        });
      });
    }

    /* ---- rendering ---- */

    function paint(index, card) {
      var face = seats[index].querySelector('[data-face]');
      var rank = card[0];
      var suit = card[1];
      var red = suit === 'h' || suit === 'd';
      face.className = 'face' + (red ? ' face--red' : '');
      face.innerHTML = '<span class="corner">' + (LONG[rank] || rank)
        + '<span>' + PIP[suit] + '</span></span>'
        + '<span class="pip">' + PIP[suit] + '</span>';
      seats[index].setAttribute('aria-label',
        (LONG[rank] || rank) + ' of ' + { s: 'spades', h: 'hearts',
          d: 'diamonds', c: 'clubs' }[suit]);
    }

    function show(index) { seats[index].classList.add('seat--up'); }

    function paytable(rows, sample, lit) {
      var body = root.querySelector('[data-paytable]');
      var note = root.querySelector('[data-odds-note]');
      if (body) {
        body.innerHTML = rows.map(function (row) {
          // "1 in N" only for the genuinely rare hands. At the boundary it
          // read badly: a flush at 0.9998% became "1 in 100" sitting directly
          // under a full house at "1.1%", which are the same size.
          var chance = row.percent >= 0.5
            ? row.percent.toFixed(1) + '%'
            : '1 in ' + Math.round(row.one_in).toLocaleString();
          return '<tr' + (row.tier === lit ? ' class="lit"' : '') + '>'
            + '<th scope="row">' + row.hand + '</th>'
            + '<td>' + row.label + '</td>'
            + '<td class="num">' + chance + '</td></tr>';
        }).join('');
      }
      if (note && sample) {
        note.textContent = 'A pair only pays from jacks up. These chances were '
          + 'measured over ' + sample.toLocaleString() + ' hands played to the '
          + 'strategy behind the hint button — your decisions are real, so '
          + 'playing better than it beats them.';
      }
    }

    /* ---- the three moves ---- */

    function deal() {
      action.disabled = true;
      say('Shuffling…');
      var seedField = root.querySelector('[data-client-seed]');
      var clientSeed = (seedField && seedField.value.trim()) || browserId().slice(0, 10);

      proxy('/deal', { nonce: round.nonce, client_seed: clientSeed })
        .then(function (data) {
          if (data.error) {
            say(data.error);
            action.disabled = data.spent === true;
            return;
          }
          hand = data.hand;
          advice = data.advice || [];
          held = {};
          phase = 'dealt';
          handRow.classList.add('hand--live');

          hand.forEach(function (card, index) {
            paint(index, card);
            // Staggered so it reads as cards being dealt one at a time rather
            // than a table that simply changed state.
            window.setTimeout(function () { show(index); }, index * 110);
          });

          window.setTimeout(function () {
            seats.forEach(function (seat) { seat.disabled = false; });
            action.textContent = 'Draw';
            action.disabled = false;
            hintButton.hidden = false;
            say('Tap the cards you want to keep, then draw.');
          }, hand.length * 110 + 340);
        })
        .catch(function (error) {
          say(error.message || 'The table is closed. Try again in a moment.');
          action.disabled = false;
        });
    }

    function draw() {
      action.disabled = true;
      hintButton.hidden = true;
      seats.forEach(function (seat) { seat.disabled = true; });
      handRow.classList.remove('hand--live');
      var holds = Object.keys(held).filter(function (k) { return held[k]; })
        .map(Number).sort();
      say('Drawing…');

      proxy('/draw', { nonce: round.nonce, holds: holds })
        .then(function (data) {
          if (data.error) { say(data.error); return; }
          phase = 'done';
          var replaced = [0, 1, 2, 3, 4].filter(function (i) {
            return holds.indexOf(i) === -1;
          });
          // Turn the replaced cards face down, swap the face, turn them back.
          replaced.forEach(function (index, n) {
            seats[index].classList.remove('seat--up');
            window.setTimeout(function () {
              paint(index, data.final[index]);
              show(index);
            }, 260 + n * 110);
          });

          window.setTimeout(function () {
            finish(data);
          }, 300 + replaced.length * 110 + 420);
        })
        .catch(function (error) {
          say(error.message || 'That did not go through.');
          action.disabled = false;
        });
    }

    function finish(data) {
      var prize = data.prize || {};
      root.querySelector('[data-res-hand]').textContent = prize.hand || '';
      root.querySelector('[data-res-prize]').textContent = prize.label || '';
      root.querySelector('[data-res-blurb]').textContent = prize.blurb || '';
      result.hidden = false;
      action.hidden = true;
      // Holding is over, so the hold marks come off - otherwise a blue "kept"
      // ring argues with the green "this is what paid" ring on the same card.
      seats.forEach(function (seat) {
        seat.classList.remove('seat--held');
        seat.setAttribute('aria-pressed', 'false');
      });
      say(data.tier === 'nothing'
        ? 'Nothing that pays this time — but nobody leaves empty-handed.'
        : prize.hand + '.');

      if (data.tier !== 'nothing') {
        // Light the cards that made the hand, so the payout is legible rather
        // than asserted.
        winners(data.final, data.tier).forEach(function (index) {
          seats[index].classList.add('seat--win');
        });
      }
      paytable(round.paytable, round.sample, data.tier);

      var skill = root.querySelector('[data-skill]');
      if (skill && data.skill) {
        root.querySelector('[data-skill-q]').textContent = data.skill;
        skill.hidden = false;
      }
      proof(data);
    }

    /* Which of the five cards to outline. Ranks that repeat for the paired
     * hands, everything for the five-card hands. */
    function winners(final, tier) {
      if (['royal', 'straightf', 'flush', 'straight', 'boat'].indexOf(tier) !== -1) {
        return [0, 1, 2, 3, 4];
      }
      var counts = {};
      final.forEach(function (card) { counts[card[0]] = (counts[card[0]] || 0) + 1; });
      var wanted = Object.keys(counts).filter(function (rank) {
        if (counts[rank] < 2) return false;
        // A low pair does not pay, so it must not be lit as though it did.
        if (tier === 'jacks') return 'JQKA'.indexOf(rank) !== -1;
        return true;
      });
      return final.map(function (card, index) {
        return wanted.indexOf(card[0]) !== -1 ? index : -1;
      }).filter(function (index) { return index !== -1; });
    }

    function proof(data) {
      var revealed = root.querySelector('[data-reveal]');
      var verdict = root.querySelector('[data-proof]');
      var deckOut = root.querySelector('[data-deck]');
      if (revealed) revealed.textContent = data.server_seed;
      if (!verdict) return;
      verdict.textContent = 'Checking…';

      Promise.all([
        sha256Hex(data.server_seed),
        shuffle(data.server_seed, data.client_seed, data.nonce)
      ]).then(function (both) {
        var deck = both[1];
        var hashOk = both[0] === round.commitment;
        var dealtOk = deck.slice(0, 5).join(',') === data.hand.join(',');
        var replaced = [0, 1, 2, 3, 4].filter(function (i) {
          return data.holds.indexOf(i) === -1;
        });
        var drawnOk = replaced.every(function (seat, n) {
          return data.final[seat] === deck[5 + n];
        });
        var ok = hashOk && dealtOk && drawnOk;

        if (deckOut) {
          deckOut.textContent = deck.map(function (card) {
            return (LONG[card[0]] || card[0]) + PIP[card[1]];
          }).join(' ');
        }
        verdict.classList.toggle('ok', ok);
        verdict.classList.toggle('bad', !ok);
        verdict.textContent = ok
          ? 'Verified in your browser: the secret matches the fingerprint we '
            + 'showed you before the deal, it rebuilds this exact deck, your '
            + 'five cards are the top five, and '
            + (replaced.length
                ? 'your ' + replaced.length + ' replacement'
                  + (replaced.length > 1 ? 's came' : ' came')
                  + ' off the top of the rest.'
                : 'you stood pat.')
          : 'This did not check out. Please tell us — that is a bug, and we '
            + 'want to know.';
      }).catch(function () {
        verdict.textContent = 'Could not run the check in this browser.';
      });
    }

    /* ---- claiming ---- */

    function claim(event) {
      event.preventDefault();
      var form = event.currentTarget;
      var field = form.querySelector('input[type=email]');
      var button = form.querySelector('button[type=submit]');
      var skillField = form.querySelector('input[name=answer]');
      var skillBox = root.querySelector('[data-skill]');
      var error = root.querySelector('[data-claim-error]');
      var email = field.value.trim();
      var label = button.textContent;

      error.hidden = true;
      if (!email || email.indexOf('@') < 1) {
        error.textContent = 'We need an email address to send it to.';
        error.hidden = false;
        field.focus();
        return;
      }
      if (skillBox && !skillBox.hidden && !skillField.value.trim()) {
        error.textContent = 'Please answer the skill-testing question.';
        error.hidden = false;
        skillField.focus();
        return;
      }

      button.disabled = true;
      button.textContent = 'Working…';
      proxy('/claim', { nonce: round.nonce, email: email,
                        answer: skillField ? skillField.value.trim() : '' })
        .then(function (prize) {
          button.disabled = false;
          button.textContent = label;
          if (prize.error) {
            error.textContent = prize.error;
            error.hidden = false;
            if (prize.skill_failed && skillField) skillField.select();
            return;
          }
          form.hidden = true;
          var won = root.querySelector('[data-won]');
          root.querySelector('[data-code]').textContent =
            prize.code || (prize.currency + ' ' + prize.credit);
          root.querySelector('[data-won-note]').textContent = prize.substituted
            || prize.note
            || (prize.expires ? 'Use it by ' + prize.expires + '.' : '');
          won.hidden = false;
          won.querySelector('[data-code-copy]').focus();
        })
        .catch(function (problem) {
          button.disabled = false;
          button.textContent = label;
          error.textContent = problem.message || 'That did not go through.';
          error.hidden = false;
        });
    }

    function copyCode(event) {
      var button = event.currentTarget;
      var code = button.querySelector('[data-code]').textContent;
      if (!navigator.clipboard) return;
      navigator.clipboard.writeText(code).then(function () {
        button.classList.add('won-code--copied');
        window.setTimeout(function () {
          button.classList.remove('won-code--copied');
        }, 1600);
      }).catch(function () { /* clipboard refused; the code is still readable */ });
    }

    /* ---- wiring ---- */

    function toggle(index) {
      if (phase !== 'dealt') return;
      held[index] = !held[index];
      seats[index].classList.toggle('seat--held', held[index]);
      seats[index].setAttribute('aria-pressed', held[index] ? 'true' : 'false');
    }

    seats.forEach(function (seat, index) {
      seat.addEventListener('click', function () { toggle(index); });
    });

    action.addEventListener('click', function () {
      if (phase === 'ready') return deal();
      if (phase === 'dealt') return draw();
    });

    hintButton.addEventListener('click', function () {
      if (phase !== 'dealt') return;
      seats.forEach(function (seat, index) {
        var want = advice.indexOf(index) !== -1;
        if (!!held[index] !== want) toggle(index);
      });
      say(advice.length
        ? 'The house would keep ' + advice.length + '.'
        : 'The house would throw all five away.');
    });

    root.querySelector('[data-claim]').addEventListener('submit', claim);
    root.querySelector('[data-code-copy]').addEventListener('click', copyCode);

    proxy('/start')
      .then(function (data) {
        if (data.error) { say(data.error); return; }
        round = data;
        paytable(data.paytable, data.sample, null);
        var commitment = root.querySelector('[data-commitment]');
        if (commitment) commitment.textContent = data.commitment;
        action.disabled = false;
        say(idle);
      })
      .catch(function () {
        say('The table is being reset. Please come back shortly.');
        action.disabled = true;
      });
  }

  function boot() { document.querySelectorAll('[data-poker]').forEach(table); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}());
